import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '~/modules/hooks';
import { selectUser } from '~/selectors';
import { setPaymentCompleted, setPendingPlan } from '~/store/slices/user';
import { extractPlanFromUrl } from '~/utils/payment';

/**
 * Custom hook to handle payment plan detection and state management
 * Only handles URL-based plan initiation, doesn't interfere with programmatically set plans
 */
export const usePaymentPlan = () => {
  const dispatch = useDispatch();
  const user = useAppSelector(selectUser);

  useEffect(() => {
    // Check URL for payment plan parameter
    const urlPlan = extractPlanFromUrl();

    if (urlPlan) {
      // URL has a valid plan parameter - set it
      dispatch(setPaymentCompleted(false));
      dispatch(setPendingPlan(urlPlan));

      // Clean URL by removing the parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('initiate_plan');
      window.history.replaceState({}, '', url.toString());
    } else if (new URLSearchParams(window.location.search).has('initiate_plan')) {
      // URL has invalid plan parameter - clear it
      dispatch(setPendingPlan(null));

      // Clean URL by removing the invalid parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('initiate_plan');
      window.history.replaceState({}, '', url.toString());
    }
    // No URL parameter - do nothing (preserve existing state)
  }, [dispatch]);

  return {
    pendingPlan: user.pendingPlan,
    paymentInitiated: user.paymentInitiated,
    paymentCompleted: user.paymentCompleted,
  };
};

/**
 * Hook specifically for checking if payment should be initiated
 */
export const useShouldInitiatePayment = () => {
  const user = useAppSelector(selectUser);
  const { pendingPlan, paymentCompleted } = usePaymentPlan();

  return {
    shouldInitiate: !!(pendingPlan && !paymentCompleted && user.isAuthenticated),
    plan: pendingPlan,
  };
};
